import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  FileText, Clock, CheckCircle, AlertCircle, 
  Search, Filter, ArrowRight, Download, CalendarDays, X
} from 'lucide-react';
import { adminApi, employeeApi } from '../lib/api';
import CountryFlag from '../components/CountryFlag';
import api from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';

const AdminApplications = ({ isEmployee = false }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [partners, setPartners] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [perPage, setPerPage] = useState(25);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const status = searchParams.get('status');
    const assignedOnly = searchParams.get('assigned_only');
    
    if (status) setStatusFilter(status);

    const fetchApplications = async () => {
      try {
        const params = {
          page: pagination.page,
          limit: perPage,
          ...(statusFilter !== 'all' && { status: statusFilter }),
          ...(searchQuery && { search: searchQuery }),
          ...(assignedOnly && { assigned_only: assignedOnly === 'true' })
        };
        
        const apiClient = isEmployee ? employeeApi : adminApi;
        const response = await apiClient.getApplications(params);
        
        setApplications(response.data.applications || []);
        setPagination({
          page: response.data.page,
          pages: response.data.pages,
          total: response.data.total
        });
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, [pagination.page, perPage, statusFilter, searchQuery, isEmployee, searchParams]);

  // Fetch partners list for filter dropdown
  useEffect(() => {
    if (!isEmployee) {
      api.get('/admin/partners').then(res => setPartners(res.data)).catch(() => {});
    }
  }, [isEmployee]);

  // Client-side source/partner/date filtering
  const filteredApplications = applications.filter(app => {
    if (sourceFilter === 'partner' && !app.submitted_by_partner) return false;
    if (sourceFilter === 'candidate' && app.submitted_by_partner) return false;
    if (partnerFilter !== 'all' && app.submitted_by_partner !== partnerFilter) return false;
    if (dateFrom) {
      const appDate = new Date(app.created_at);
      const from = new Date(dateFrom); from.setHours(0,0,0,0);
      if (appDate < from) return false;
    }
    if (dateTo) {
      const appDate = new Date(app.created_at);
      const to = new Date(dateTo); to.setHours(23,59,59,999);
      if (appDate > to) return false;
    }
    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500 text-white';
      case 'rejected': return 'bg-red-500 text-white';
      case 'submitted':
      case 'under_review':
      case 'processing':
        return 'bg-[#FF5A36] text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const [editingEnd, setEditingEnd] = useState(false);
  const [editEndValue, setEditEndValue] = useState('');

  const hasClientFilter = sourceFilter !== 'all' || partnerFilter !== 'all' || dateFrom || dateTo;
  const displayTotal = hasClientFilter ? filteredApplications.length : pagination.total;
  const startItem = hasClientFilter ? (filteredApplications.length > 0 ? 1 : 0) : ((pagination.page - 1) * perPage) + 1;
  const endItem = hasClientFilter ? filteredApplications.length : Math.min(pagination.page * perPage, pagination.total);
  const displayPages = hasClientFilter ? 1 : pagination.pages;

  const handleEndValueSubmit = () => {
    const val = parseInt(editEndValue);
    if (val && val > 0 && val <= pagination.total) {
      setPerPage(val);
      setPagination(p => ({ ...p, page: 1 }));
    }
    setEditingEnd(false);
  };

  const statuses = ['all', 'draft', 'submitted', 'under_review', 'processing', 'approved', 'rejected'];

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar />
      
      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="admin-applications-page">
        {/* Header with count and pagination */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {isEmployee ? 'Applications' : 'All Applications'}
            </h1>
            <p className="text-[#4A5568] mt-1">{displayTotal} Applications</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Editable Pagination: 1-25 of 76 */}
            <div className="flex items-center gap-1.5 bg-white border border-[#D1CBBF] rounded-xl px-4 py-2" data-testid="top-pagination">
              <span className="text-sm text-[#0A1E40] font-medium">{startItem}</span>
              <span className="text-sm text-[#4A5568]">-</span>
              {editingEnd ? (
                <input
                  type="number"
                  className="w-14 text-sm text-center font-bold text-[#FF5A36] border border-[#FF5A36] rounded px-1 py-0.5 outline-none"
                  value={editEndValue}
                  onChange={(e) => setEditEndValue(e.target.value)}
                  onBlur={handleEndValueSubmit}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleEndValueSubmit(); }}
                  autoFocus
                  data-testid="edit-end-input"
                />
              ) : (
                <button
                  onClick={() => { setEditingEnd(true); setEditEndValue(String(endItem)); }}
                  className="text-sm font-bold text-[#FF5A36] hover:underline cursor-pointer"
                  data-testid="editable-end"
                  title="Click to change per page"
                >
                  {endItem}
                </button>
              )}
              <span className="text-sm text-[#4A5568]">of</span>
              <button
                onClick={() => { setPerPage(displayTotal || 999); setPagination(p => ({ ...p, page: 1 })); }}
                className="text-sm font-bold text-[#0A1E40] hover:text-[#FF5A36] hover:underline cursor-pointer"
                title="Click to show all"
                data-testid="show-all-btn"
              >
                {displayTotal}
              </button>
            </div>

            {/* Prev / Next */}
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page === 1}
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                className="border-[#D1CBBF] h-9"
              >
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= displayPages}
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                className="border-[#D1CBBF] h-9"
              >
                Next
              </Button>
            </div>

            <Button variant="outline" className="border-[#D1CBBF] h-9">
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-5 border border-[#D1CBBF] mb-6 space-y-4">
          {/* Row 1: Search */}
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] pointer-events-none" />
            <Input
              type="text"
              placeholder="Search by application ID, name, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 w-full"
              data-testid="search-applications-input"
            />
          </div>

          {/* Row 2: Date, Status, Source, Partner */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Date From */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 border-[#D1CBBF] text-sm font-normal" data-testid="date-from-btn">
                  <CalendarDays className="w-3.5 h-3.5 mr-2 text-[#FF5A36]" />
                  {dateFrom ? format(dateFrom, 'dd MMM yyyy') : 'From Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} /></PopoverContent>
            </Popover>

            {/* Date To */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 border-[#D1CBBF] text-sm font-normal" data-testid="date-to-btn">
                  <CalendarDays className="w-3.5 h-3.5 mr-2 text-[#FF5A36]" />
                  {dateTo ? format(dateTo, 'dd MMM yyyy') : 'To Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={dateTo} onSelect={setDateTo} disabled={(date) => dateFrom && date < dateFrom} /></PopoverContent>
            </Popover>

            {/* Clear dates */}
            {(dateFrom || dateTo) && (
              <Button variant="ghost" size="sm" className="h-9 text-[#4A5568] hover:text-red-500" onClick={() => { setDateFrom(null); setDateTo(null); }}>
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}

            <div className="h-6 w-px bg-[#D1CBBF] hidden md:block" />

            {/* Status Dropdown */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px] h-9" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map(s => (
                  <SelectItem key={s} value={s} className="capitalize">{s === 'all' ? 'All Status' : s.replace('_', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Source Dropdown */}
            {!isEmployee && (
              <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); if (v !== 'partner') setPartnerFilter('all'); }}>
                <SelectTrigger className="w-[150px] h-9" data-testid="source-filter">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="partner">Partner</SelectItem>
                  <SelectItem value="candidate">Candidate</SelectItem>
                </SelectContent>
              </Select>
            )}

            {/* Partner Dropdown (when source = partner) */}
            {!isEmployee && sourceFilter === 'partner' && partners.length > 0 && (
              <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                <SelectTrigger className="w-[200px] h-9" data-testid="partner-filter-select">
                  <SelectValue placeholder="All Partners" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Partners</SelectItem>
                  {partners.map(p => (
                    <SelectItem key={p.user_id} value={p.user_id}>{p.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Applications */}
        {loading ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-[#D1CBBF]">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-[#D1CBBF]">
            <FileText className="w-12 h-12 text-[#D1CBBF] mx-auto mb-4" />
            <p className="text-[#4A5568]">No applications found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((app) => {
              const clientName = app.user?.name || app.applicant?.name || app.travelers?.[0]?.first_name || 'Unknown';
              const clientEmail = app.user?.email || app.applicant?.email || '';
              const isPaid = app.payment_status === 'completed';
              const basePath = isEmployee ? '/employee' : '/admin';
              return (
              <div key={app.application_id} className="bg-white rounded-2xl border border-[#D1CBBF] overflow-hidden hover:shadow-md transition-all" data-testid={`app-row-${app.application_id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-11 h-11 rounded-xl bg-[#0A1E40] flex items-center justify-center shrink-0">
                        <span className="text-white font-bold text-sm">{clientName.charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-[#0A1E40] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{clientName}</h3>
                        <div className="flex items-center gap-2 text-xs text-[#4A5568]">
                          <CountryFlag code={app.country?.code} size="xs" />
                          <span>{app.country?.name}</span>
                          <span className="text-[#D1CBBF]">·</span>
                          <span>{app.visa_type?.name}</span>
                          {clientEmail && (
                            <>
                              <span className="text-[#D1CBBF]">·</span>
                              <span className="truncate max-w-[180px]">{clientEmail}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {app.submitted_by_partner ? (
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase bg-[#0A1E40] text-white">Partner</span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase bg-[#F5F0E6] text-[#4A5568]">Candidate</span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${getStatusColor(app.status)}`}>
                        {app.status?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3 bg-[#F5F0E6] rounded-xl p-3">
                    <div>
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">App ID</p>
                      <Link to={`${basePath}/applications/${app.application_id}`} className="text-xs text-[#FF5A36] font-bold hover:underline">{app.application_id}</Link>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Source</p>
                      <p className="text-xs font-bold text-[#0A1E40]">{app.submitted_by_partner ? (app.partner_company || 'Partner') : 'Direct'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Travelers</p>
                      <p className="text-xs font-bold text-[#0A1E40]">{app.num_travelers || 1}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Fee</p>
                      <p className="text-xs font-bold text-[#0A1E40]">₹{app.total_fee?.toLocaleString() || '0'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Payment</p>
                      <p className={`text-xs font-bold ${isPaid ? 'text-green-600' : 'text-[#FF5A36]'}`}>{isPaid ? 'Paid' : app.payment_status || 'Pending'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Assigned</p>
                      <p className="text-xs font-bold text-[#0A1E40]">{app.assigned_to || 'Unassigned'}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F5F0E6]">
                    <span className="text-xs text-[#4A5568]">Created: {new Date(app.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    <Link to={`${basePath}/applications/${app.application_id}`} className="text-xs text-[#FF5A36] font-semibold hover:underline flex items-center gap-1">
                      View Details <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

      </main>
    </div>
  );
};

export default AdminApplications;
